import { styled, keyframes } from "styled-components";

const marquee = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

export const OurServicesStyled = styled.section`
  padding: 140px 0;
  background: var(--bg-primary);
  overflow: hidden;
  position: relative;

  .marqueeWrapper {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    overflow: hidden;
    transform: translateY(-50%);
    z-index: 0;
    pointer-events: none;

    .marqueeTrack {
      display: flex;
      width: max-content;
      animation: ${marquee} 30s linear infinite;

      .marqueeText {
        font-family: Poppins;
        font-size: 200px;
        font-weight: 900;
        color: var(--text-primary);
        opacity: 0.02;
        white-space: nowrap;
        padding: 0 40px;
        text-transform: uppercase;
      }
    }
  }

  .ourServicesContainer {
    max-width: 1400px;
    width: 100%;
    margin: 0 auto;
    padding: 0 40px;
    position: relative;
    z-index: 1;

    .sectionHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-bottom: 80px;
      gap: 40px;

      .headerLeft {
        max-width: 600px;

        .sectionLabel {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;

          .line {
            width: 40px;
            height: 3px;
            background: var(--accent);
            border-radius: 2px;
          }

          span {
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 3px;
            color: var(--accent);
          }
        }

        h1 {
          font-family: Poppins;
          font-size: 56px;
          font-weight: 800;
          line-height: 1.1;
          color: var(--text-primary);
          margin: 0;

          .highlight {
            background: linear-gradient(135deg, var(--accent) 0%, var(--primary) 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
        }
      }

      .headerRight {
        p {
          font-family: Poppins;
          font-size: 18px;
          line-height: 1.7;
          color: var(--text-secondary);
          max-width: 400px;
        }
      }
    }

    .serviceItemsContainer {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 30px;
    }

    @media (max-width: 1024px) {
      padding: 0 30px;

      .sectionHeader {
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 60px;

        .headerLeft h1 {
          font-size: 42px;
        }
      }

      .serviceItemsContainer {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 640px) {
      padding: 0 20px;

      .sectionHeader .headerLeft h1 {
        font-size: 32px;
      }

      .serviceItemsContainer {
        grid-template-columns: 1fr;
        max-width: 400px;
        margin: 0 auto;
      }
    }
  }
`;

export const OurServicesCard = styled.div`
  cursor: pointer;
  background: var(--card-bg);
  border-radius: 24px;
  overflow: hidden;
  border: 2px solid var(--card-border);
  transition: all 0.4s ease;
  position: relative;
  will-change: transform, opacity;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      transparent 0%,
      transparent 50%,
      rgba(0, 0, 0, 0.8) 100%
    );
    z-index: 1;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover {
    transform: translateY(-12px) scale(1.02);
    border-color: var(--accent);
    box-shadow: 0 30px 60px var(--shadow-large);

    &::before {
      opacity: 1;
    }

    .imageWrapper img {
      transform: scale(1.1);
    }

    .contentWrapper {
      transform: translateY(-20px);
      opacity: 1;
    }

    .serviceNumber {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .imageWrapper {
    height: 350px;
    overflow: hidden;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.6s ease;
    }
  }

  .serviceNumber {
    position: absolute;
    top: 20px;
    right: 20px;
    font-family: Poppins;
    font-size: 72px;
    font-weight: 900;
    color: #fff;
    opacity: 0;
    transform: translateY(-20px);
    transition: all 0.4s ease;
    z-index: 2;
    line-height: 1;
    text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .contentWrapper {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 30px;
    z-index: 2;
    transform: translateY(30px);
    opacity: 0;
    transition: all 0.4s ease;

    h3 {
      color: #fff;
      font-family: Poppins;
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 12px 0;
    }

    p {
      font-family: Poppins;
      color: rgba(255, 255, 255, 0.8);
      font-size: 14px;
      line-height: 1.6;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .viewLink {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #fff;
      font-family: Poppins;
      font-size: 14px;
      font-weight: 600;
      margin-top: 16px;
      text-decoration: none;

      svg {
        width: 16px;
        height: 16px;
        transition: transform 0.3s ease;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }
  }

  /* Default visible state for non-hover */
  .defaultContent {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 24px;
    background: var(--card-bg);
    z-index: 1;
    transition: opacity 0.3s ease;

    h3 {
      color: var(--text-primary);
      font-family: Poppins;
      font-size: 20px;
      font-weight: 700;
      margin: 0;
    }
  }

  &:hover .defaultContent {
    opacity: 0;
  }
`;
